/**
 * MerchantBannerResults — merchant-grade results for self-serve banner tests
 * (B7, spec §3.5). Three states in plain Spanish, no stats internals:
 * Recolectando / Veredicto / Detenido.
 *
 * Reads the same response fields the consultant DecisionHelper reads —
 * top-level bb_verdict + the goal-aware flat sessions/conversion_rate maps —
 * with the same per-arm minimum-sample gate deciding "keep collecting" vs a
 * verdict. Experiments are identified by the self_serve_banner marker the
 * wizard writes into the treatment config; drift pauses surface via
 * auto_stopped_reason 'control_drift' (banner-drift.js).
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text } from '@nimbus-ds/components'
import { experimentService } from '@/api/experiments'
import { trackAppEvent } from '@/lib/appTelemetry'
import type { Experiment } from '@/types'

// Same bar the consultant DecisionHelper uses (MIN_SAMPLE_SIZE): below this
// per arm we say "recolectando" no matter what the engine's verdict is.
const MIN_SESSIONS_PER_ARM = 100

export function isSelfServeBannerExperiment(exp: Experiment): boolean {
  return !!(exp.variants as any)?.treatment?.config?.self_serve_banner
}

type CardState =
  | { kind: 'collecting'; sessions: number; endDate: string | null }
  | { kind: 'verdict_treatment'; controlRate: number; treatmentRate: number; running: boolean }
  | { kind: 'verdict_control'; controlRate: number; treatmentRate: number }
  | { kind: 'no_difference' }
  | { kind: 'stopped_drift' }
  | { kind: 'stopped_other' }

function deriveState(exp: Experiment, results: any | null): CardState {
  if (exp.status === 'paused') {
    return exp.auto_stopped_reason === 'control_drift'
      ? { kind: 'stopped_drift' }
      : { kind: 'stopped_other' }
  }

  const sessions = results?.sessions || {}
  const rates = results?.conversion_rate || {}
  const minSampleReached =
    (sessions.control || 0) >= MIN_SESSIONS_PER_ARM &&
    (sessions.treatment || 0) >= MIN_SESSIONS_PER_ARM
  const verdict = results?.bb_verdict
  const totalSessions = (sessions.control || 0) + (sessions.treatment || 0)

  if (verdict === 'treatment_better' && minSampleReached) {
    return {
      kind: 'verdict_treatment',
      controlRate: rates.control || 0,
      treatmentRate: rates.treatment || 0,
      running: exp.status === 'active',
    }
  }
  if (verdict === 'control_better' && minSampleReached) {
    return { kind: 'verdict_control', controlRate: rates.control || 0, treatmentRate: rates.treatment || 0 }
  }
  if (exp.status === 'completed') {
    return { kind: 'no_difference' }
  }
  return {
    kind: 'collecting',
    sessions: totalSessions,
    endDate: exp.timing?.end_date_time || null,
  }
}

const per100 = (rate: number) => {
  const v = rate * 100
  return v >= 10 ? Math.round(v) : Math.round(v * 10) / 10
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' }) : null

function StateCard({ exp, state }: { exp: Experiment; state: CardState }) {
  let emoji = '🔎'
  let title = ''
  let body = ''
  let caption: string | null = null

  switch (state.kind) {
    case 'collecting': {
      emoji = '🔎'
      title = 'Recolectando datos'
      body =
        state.sessions > 0
          ? `Ya participaron ${state.sessions.toLocaleString('es-AR')} visitas. Necesitamos un poco más para darte una respuesta confiable.`
          : 'La prueba está en marcha. En cuanto tus visitas empiecen a participar, vas a ver el avance acá.'
      const end = fmtDate(state.endDate)
      caption = end ? `La prueba termina como máximo el ${end}.` : null
      break
    }
    case 'verdict_treatment':
      emoji = '🏆'
      title = 'Ganó el banner nuevo'
      body =
        `El banner nuevo genera más clics: ${per100(state.treatmentRate)} de cada 100 visitas le hacen clic, ` +
        `contra ${per100(state.controlRate)} del actual.`
      caption = state.running
        ? 'La prueba sigue mostrando el ganador a la mitad de tus visitas. Para dejarlo fijo, subilo como banner desde el administrador de tu tienda.'
        : 'Para aprovecharlo, subilo como tu banner desde el administrador de tu tienda.'
      break
    case 'verdict_control':
      emoji = '✅'
      title = 'Tu banner actual sigue siendo el mejor'
      body =
        `El banner nuevo no superó al actual (${per100(state.treatmentRate)} contra ${per100(state.controlRate)} clics ` +
        'cada 100 visitas). Buen dato: ahora lo sabés por una prueba real, no por una corazonada.'
      break
    case 'no_difference':
      emoji = '🤝'
      title = 'No hubo diferencia clara'
      body =
        'Ninguno de los dos banners generó claramente más clics con el tráfico del período. No es un mal resultado — es honesto. Podés usar el que prefieras, o probar una idea más distinta.'
      break
    case 'stopped_drift':
      emoji = '⏸️'
      title = 'La prueba se detuvo'
      body =
        'Cambiaste el banner de tu tienda mientras la prueba corría, así que los resultados dejaron de ser comparables y la detuvimos. Podés iniciar una prueba nueva cuando quieras.'
      break
    case 'stopped_other':
      emoji = '⏸️'
      title = 'La prueba está pausada'
      body = 'Si no la pausaste vos, escribinos y lo vemos juntos.'
      break
  }

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none">{emoji}</span>
          <div className="min-w-0 flex-1 space-y-1">
            <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
              {title}
            </Text>
            <Text>{body}</Text>
            {caption && <Text fontSize="caption">{caption}</Text>}
            <Text fontSize="caption">
              Iniciada el {new Date(exp.created_at).toLocaleDateString('es-AR')}
            </Text>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export function MerchantBannerResults() {
  const [items, setItems] = useState<{ exp: Experiment; state: CardState }[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    trackAppEvent('results_viewed')
    let cancelled = false
    const load = async () => {
      try {
        const all = await experimentService.getExperiments()
        const mine = all
          .filter(isSelfServeBannerExperiment)
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
          .slice(0, 5)
        const withResults = await Promise.all(
          mine.map(async (exp) => {
            const results = await experimentService.getExperimentResults(exp.id).catch(() => null)
            return { exp, state: deriveState(exp, results) }
          })
        )
        if (!cancelled) setItems(withResults)
      } catch {
        if (!cancelled) setError('No pudimos cargar tus pruebas. Probá de nuevo en un rato.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-4">
      <div>
        <Link to="/merchant" className="text-sm text-muted-foreground hover:text-foreground">
          ← Volver
        </Link>
        <div className="mt-2">
          <Text fontWeight="bold" fontSize="highlight" color="neutral-textHigh">
            Tus pruebas de banner
          </Text>
        </div>
      </div>

      {loading && <Text className="text-center">Cargando…</Text>}
      {error && (
        <Card>
          <Card.Body>
            <Text className="text-center">{error}</Text>
          </Card.Body>
        </Card>
      )}
      {!loading && !error && items.length === 0 && (
        <Card>
          <Card.Body>
            <Text className="text-center">
              Todavía no iniciaste ninguna prueba de banner.{' '}
              <Link to="/merchant/banner-test" className="underline">
                Empezá acá.
              </Link>
            </Text>
          </Card.Body>
        </Card>
      )}
      {items.map(({ exp, state }) => (
        <StateCard key={exp.id} exp={exp} state={state} />
      ))}
    </div>
  )
}
